import {
  CircularProgress,
  Grid,
  LinearProgress,
  makeStyles,
  Toolbar,
  Typography
} from "@material-ui/core";
import { useQuery } from "@redux-requests/react";
import { Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { CardItem, createImageFromInitials, Header, Loader } from "../../components";
import { AutocompleteField } from "../../components/formik";
import { getProfiles, getProfilesNames } from "../../ducks/actions";
import { ReactComponent as MeetTheExpertSvg } from "../../assets/icons/meet the expert.svg";
import * as types from "../../ducks/types";

const useStyles = makeStyles(() => ({
  filterToolbar: {
    marginTop: "1rem"
  },
  profilesGrid: {
    marginTop: "1rem"
  },
  infiniteScroll: {
    overflow: "hidden !important",
    marginBottom: "4rem"
  },
  progressiveProfilesLoader: {
    margin: "1rem 0",
    display: "flex",
    justifyContent: "center"
  }
}));

export default function TeachersPage() {
  const classes = useStyles();
  const intl = useIntl();
  const history = useHistory();

  const [filterProfileFullName, setFilterProfileFullName] = useState(null);

  const [profilesPage, setProfilesPage] = useState(1);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      getProfiles({ role: "teacher", page: profilesPage, perPage: 12, filterProfileFullName })
    );
  }, [dispatch, profilesPage, filterProfileFullName]);

  useEffect(() => {
    dispatch(getProfilesNames({ role: "teacher" }));
  }, [dispatch]);

  const {
    data: {
      data: profiles,
      meta: { total: totalProfilesPages, current_page: fetchedCurrentPage }
    },
    loading: profilesLoading
  } = useQuery({ type: types.GET_PROFILES, defaultData: { data: [], meta: { total: 0 } } });

  const {
    data: { data: profilesNames },
    loading: profilesNamesLoading
  } = useQuery({ type: types.GET_PROFILES_NAMES, defaultData: { data: [] } });

  const filterNameOptions = useMemo(() => {
    return (
      profilesNames?.map(profile => ({
        label: `${profile.fullName}`,
        value: profile.fullName
      })) || []
    );
  }, [profilesNames]);

  const onFilterSubmit = ({ profileFullName }) => {
    setProfilesPage(1);
    setFilterProfileFullName(profileFullName);
  };

  const onCardClick = profileId => () => {
    history.push(`teachers/${profileId}`);
  };

  const nextProfilesPage = () => {
    setProfilesPage(parseInt(profilesPage, 10) + 1);
  };

  return (
    <>
      <InfiniteScroll
        className={classes.infiniteScroll}
        scrollableTarget="scrollerContent"
        dataLength={profiles.length}
        next={nextProfilesPage}
        hasMore={profilesPage < totalProfilesPages}
        loader={
          <>
            <LinearProgress />
            <br />
          </>
        }
      >
        <Header
          icon={MeetTheExpertSvg}
          title={intl.formatMessage({
            id: "Pages.Teachers.AskToTeacher",
            defaultMessage: "Ask to the Teacher"
          })}
          helper={intl.formatMessage({
            id: "Pages.Teachers.Description",
            defaultMessage: "test description"
          })}
        />
        <Loader loading={profilesLoading || profilesNamesLoading}>
          <Formik
            initialValues={{
              profileFullName: filterProfileFullName || ""
            }}
            onSubmit={onFilterSubmit}
          >
            <Toolbar className={classes.filterToolbar}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={2} lg={1}>
                  <Typography variant="subtitle1">
                    <strong>
                      {intl.formatMessage({
                        id: "Pages.Teachers.FilterBy",
                        defaultMessage: "Filter by"
                      })}
                    </strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={5} lg={3} xl={2}>
                  <AutocompleteField
                    name="profileFullName"
                    variant="outlined"
                    label={intl.formatMessage({
                      id: "Pages.Teachers.Name",
                      defaultMessage: "Name"
                    })}
                    options={filterNameOptions}
                    submitOnChange
                  />
                </Grid>
              </Grid>
            </Toolbar>
          </Formik>

          <Grid className={classes.profilesGrid} container spacing={3}>
            {profiles?.map(profile => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={profile.id}>
                <CardItem
                  title={`${profile.firstName} ${profile.lastName}`}
                  description={`${profile.title}`}
                  thumbUrl={
                    profile.avatar ||
                    createImageFromInitials(300, `${profile.firstName} ${profile.lastName}`, "grey")
                  }
                  onClick={onCardClick(profile.id)}
                />
              </Grid>
            ))}
          </Grid>

          {profilesLoading ||
            (profilesPage !== fetchedCurrentPage && (
              <div className={classes.progressiveProfilesLoader}>
                <CircularProgress size="3rem" />
              </div>
            ))}
        </Loader>
      </InfiniteScroll>
    </>
  );
}
